<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TaxiClientsAPI from 'dashboard/api/taxiClients';
import TaxiPartnersAPI from 'dashboard/api/taxiPartners';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();

const clients = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const clientDialogRef = ref(null);

const form = ref({
  first_name: '',
  last_name: '',
  phone: '',
});

const persistedClientId = ref(null);
const trips = ref([]);
const tripDraft = ref({
  trip_on: '',
  notes: '',
});

const partnersCatalog = ref([]);
const selectedPartners = ref([]);

const syncSelectedPartnersFromClient = client => {
  const raw = client.taxi_partners || [];
  const byId = new Map(partnersCatalog.value.map(p => [p.id, p]));
  selectedPartners.value = raw.map(
    p => byId.get(p.id) || { id: p.id, name: p.name }
  );
};

const filteredClients = computed(() => {
  if (!searchQuery.value.trim()) return clients.value;
  const q = searchQuery.value.toLowerCase();
  return clients.value.filter(c => {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    return (
      name.includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      (c.taxi_partners || []).some(p => p.name?.toLowerCase().includes(q)) ||
      (c.trips || []).some(tr => tr.notes?.toLowerCase().includes(q))
    );
  });
});

const resetForm = () => {
  form.value = {
    first_name: '',
    last_name: '',
    phone: '',
  };
  persistedClientId.value = null;
  selectedPartners.value = [];
  trips.value = [];
  tripDraft.value = { trip_on: '', notes: '' };
};

const fetchClients = async () => {
  loading.value = true;
  try {
    const { data } = await TaxiClientsAPI.getAll();
    clients.value = data;
  } catch {
    useAlert(t('TAXIS.CLIENTS.API.ERROR_FETCH'));
  } finally {
    loading.value = false;
  }
};

const fetchPartnersCatalog = async () => {
  try {
    const { data } = await TaxiPartnersAPI.getAll();
    partnersCatalog.value = (data || []).map(p => ({ id: p.id, name: p.name }));
  } catch {
    partnersCatalog.value = [];
  }
};

onMounted(async () => {
  await Promise.all([fetchPartnersCatalog(), fetchClients()]);
});

const openCreate = () => {
  resetForm();
  clientDialogRef.value?.open();
};

const openEdit = client => {
  resetForm();
  persistedClientId.value = client.id;
  form.value = {
    first_name: client.first_name || '',
    last_name: client.last_name || '',
    phone: client.phone || '',
  };
  trips.value = [...(client.trips || [])];
  syncSelectedPartnersFromClient(client);
  clientDialogRef.value?.open();
};

const onDialogClose = () => {
  resetForm();
};

const closeDialog = () => {
  clientDialogRef.value?.close();
};

const saveClient = async () => {
  if (!form.value.first_name.trim() || !form.value.last_name.trim()) {
    useAlert(t('TAXIS.CLIENTS.VALIDATION.NAME_REQUIRED'));
    return;
  }
  try {
    const payload = {
      first_name: form.value.first_name.trim(),
      last_name: form.value.last_name.trim(),
      phone: form.value.phone?.trim() || '',
      taxi_partner_ids: selectedPartners.value.map(p => p.id),
    };
    if (persistedClientId.value) {
      await TaxiClientsAPI.update(persistedClientId.value, payload);
      useAlert(t('TAXIS.CLIENTS.API.SUCCESS_UPDATE'));
    } else {
      const { data } = await TaxiClientsAPI.create(payload);
      persistedClientId.value = data.id;
      trips.value = data.trips || [];
      syncSelectedPartnersFromClient(data);
      useAlert(t('TAXIS.CLIENTS.API.SUCCESS_CREATE'));
    }
    await fetchClients();
  } catch {
    useAlert(t('TAXIS.CLIENTS.API.ERROR_SAVE'));
  }
};

const addTrip = async () => {
  if (!persistedClientId.value) {
    useAlert(t('TAXIS.CLIENTS.TRIPS.SAVE_CLIENT_FIRST'));
    return;
  }
  if (!tripDraft.value.trip_on) {
    useAlert(t('TAXIS.CLIENTS.TRIPS.DATE_REQUIRED'));
    return;
  }
  try {
    const { data } = await TaxiClientsAPI.createTrip(persistedClientId.value, {
      trip_on: tripDraft.value.trip_on,
      notes: tripDraft.value.notes?.trim() || '',
    });
    trips.value = [data, ...trips.value].sort((a, b) =>
      String(b.trip_on).localeCompare(String(a.trip_on))
    );
    tripDraft.value = { trip_on: '', notes: '' };
    useAlert(t('TAXIS.CLIENTS.TRIPS.API_SUCCESS_ADD'));
    await fetchClients();
  } catch {
    useAlert(t('TAXIS.CLIENTS.TRIPS.API_ERROR_ADD'));
  }
};

const removeTrip = async trip => {
  if (!persistedClientId.value) return;
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('TAXIS.CLIENTS.TRIPS.CONFIRM_DELETE'))) return;
  try {
    await TaxiClientsAPI.deleteTrip(persistedClientId.value, trip.id);
    trips.value = trips.value.filter(tr => tr.id !== trip.id);
    useAlert(t('TAXIS.CLIENTS.TRIPS.API_SUCCESS_DELETE'));
    await fetchClients();
  } catch {
    useAlert(t('TAXIS.CLIENTS.TRIPS.API_ERROR_DELETE'));
  }
};

const removeClient = async client => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('TAXIS.CLIENTS.CONFIRM_DELETE'))) return;
  try {
    await TaxiClientsAPI.delete(client.id);
    useAlert(t('TAXIS.CLIENTS.API.SUCCESS_DELETE'));
    await fetchClients();
  } catch {
    useAlert(t('TAXIS.CLIENTS.API.ERROR_DELETE'));
  }
};

const formatDate = d => {
  if (!d) return t('TAXIS.HISTORY.DASH');
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d);
  }
};

const fullName = c =>
  `${c.first_name || ''} ${c.last_name || ''}`.trim() ||
  t('TAXIS.HISTORY.DASH');

const lastTripOn = c => {
  const list = c.trips || [];
  if (!list.length) return t('TAXIS.HISTORY.DASH');
  const sorted = [...list].sort((a, b) =>
    String(b.trip_on).localeCompare(String(a.trip_on))
  );
  return formatDate(sorted[0].trip_on);
};

const tripsCount = c => (c.trips || []).length;

const partnersTitle = c => (c.taxi_partners || []).map(p => p.name).join(', ');
</script>

<template>
  <div class="flex flex-1 flex-col overflow-auto px-6 py-8">
    <div class="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-6">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div class="flex items-center gap-3">
            <div
              class="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-n-green-4/50 to-n-green-5/40 ring-1 ring-n-green-6/60"
            >
              <Icon
                icon="i-lucide-user-circle"
                class="size-5 text-n-green-11"
              />
            </div>
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ t('TAXIS.CLIENTS.TITLE') }}
            </h1>
          </div>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('TAXIS.CLIENTS.SUBTITLE') }}
          </p>
        </div>
        <Button
          :label="t('TAXIS.CLIENTS.ADD_BUTTON')"
          icon="i-lucide-plus"
          size="sm"
          color="teal"
          @click="openCreate"
        />
      </div>

      <div class="max-w-sm">
        <Input
          v-model="searchQuery"
          type="search"
          :placeholder="t('TAXIS.CLIENTS.SEARCH_PLACEHOLDER')"
          :custom-input-class="[
            'h-9 [&:not(.focus)]:!border-transparent [&:not(.focus)]:!outline-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-9',
          ]"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-11 ltr:left-2.5 rtl:right-2.5"
            />
          </template>
        </Input>
      </div>

      <div
        v-if="loading"
        class="flex flex-1 items-center justify-center py-20 text-n-slate-11"
      >
        <Spinner />
      </div>

      <div
        v-else-if="!clients.length"
        class="flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-n-green-5/30 bg-gradient-to-br from-n-green-1/30 to-transparent py-20 text-center"
      >
        <div
          class="flex size-20 items-center justify-center rounded-2xl bg-gradient-to-br from-n-green-4/40 to-n-green-5/30 ring-4 ring-n-green-4/20"
        >
          <Icon icon="i-lucide-user-circle" class="size-10 text-n-green-11" />
        </div>
        <div>
          <p class="text-lg font-semibold text-n-slate-12">
            {{ t('TAXIS.CLIENTS.EMPTY_TITLE') }}
          </p>
          <p class="mt-2 max-w-sm text-sm text-n-slate-11">
            {{ t('TAXIS.CLIENTS.EMPTY_SUBTITLE') }}
          </p>
        </div>
        <Button
          :label="t('TAXIS.CLIENTS.ADD_BUTTON')"
          icon="i-lucide-plus"
          size="md"
          color="teal"
          @click="openCreate"
        />
      </div>

      <div
        v-else
        class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div class="overflow-x-auto">
          <table class="w-full min-w-[920px] text-left text-sm">
            <thead
              class="border-b-2 border-n-green-6/30 bg-gradient-to-r from-n-green-2/40 to-n-green-3/20 text-xs font-semibold uppercase tracking-wide text-n-slate-12"
            >
              <tr>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-user"
                      class="size-3.5 text-n-green-10"
                    />
                    {{ t('TAXIS.CLIENTS.COLUMNS.NAME') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-phone"
                      class="size-3.5 text-n-blue-10"
                    />
                    {{ t('TAXIS.CLIENTS.COLUMNS.PHONE') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-handshake"
                      class="size-3.5 text-n-purple-10"
                    />
                    {{ t('TAXIS.CLIENTS.COLUMNS.PARTNERS') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon icon="i-lucide-map" class="size-3.5 text-n-teal-10" />
                    {{ t('TAXIS.CLIENTS.COLUMNS.TRIPS_COUNT') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-calendar"
                      class="size-3.5 text-n-amber-10"
                    />
                    {{ t('TAXIS.CLIENTS.COLUMNS.LAST_TRIP') }}
                  </div>
                </th>
                <th class="px-4 py-3.5 text-end">
                  {{ t('TAXIS.CLIENTS.COLUMNS.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="client in filteredClients"
                :key="client.id"
                class="transition-colors hover:bg-gradient-to-r hover:from-n-green-2/10 hover:to-transparent"
              >
                <td class="px-4 py-3.5">
                  <div class="flex items-center gap-2">
                    <div
                      class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-n-green-4/40 to-n-green-5/30 ring-1 ring-n-green-6/50"
                    >
                      <Icon
                        icon="i-lucide-user-circle"
                        class="size-4 text-n-green-11"
                      />
                    </div>
                    <span class="font-semibold text-n-slate-12">{{
                      fullName(client)
                    }}</span>
                  </div>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    v-if="client.phone"
                    class="inline-flex items-center gap-1.5 rounded-md bg-n-blue-2/50 px-2 py-1 text-sm text-n-blue-11"
                  >
                    <Icon icon="i-lucide-phone" class="size-3.5" />
                    {{ client.phone }}
                  </div>
                  <span v-else class="text-n-slate-10">{{
                    t('TAXIS.HISTORY.DASH')
                  }}</span>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    v-if="client.taxi_partners?.length"
                    class="flex flex-wrap gap-1"
                  >
                    <span
                      v-for="partner in client.taxi_partners.slice(0, 2)"
                      :key="partner.id"
                      class="inline-flex items-center gap-1 rounded-md bg-n-purple-2/40 px-2 py-0.5 text-xs text-n-purple-11"
                    >
                      <Icon icon="i-lucide-handshake" class="size-3" />
                      {{ partner.name }}
                    </span>
                    <span
                      v-if="client.taxi_partners.length > 2"
                      class="inline-flex items-center rounded-md bg-n-purple-2/40 px-2 py-0.5 text-xs font-medium text-n-purple-11"
                      :title="partnersTitle(client)"
                    >
                      {{
                        t('TAXIS.COMMON.MORE_COUNT', {
                          count: client.taxi_partners.length - 2,
                        })
                      }}
                    </span>
                  </div>
                  <span v-else class="text-n-slate-10">{{
                    t('TAXIS.HISTORY.DASH')
                  }}</span>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    class="inline-flex items-center gap-1.5 rounded-lg bg-n-teal-3/50 px-3 py-1.5 font-semibold text-n-teal-11 ring-1 ring-n-teal-6/50"
                  >
                    <Icon icon="i-lucide-map" class="size-4" />
                    <span class="text-sm">{{ tripsCount(client) }}</span>
                  </div>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    v-if="tripsCount(client) > 0"
                    class="inline-flex items-center gap-1.5 rounded-md bg-n-amber-2/50 px-2 py-1 text-sm text-n-amber-11"
                  >
                    <Icon icon="i-lucide-calendar" class="size-3.5" />
                    {{ lastTripOn(client) }}
                  </div>
                  <span v-else class="text-n-slate-10">{{
                    t('TAXIS.HISTORY.DASH')
                  }}</span>
                </td>
                <td class="px-4 py-3 text-end">
                  <div class="flex justify-end gap-1">
                    <button
                      type="button"
                      class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                      :title="t('TAXIS.CLIENTS.EDIT')"
                      @click="openEdit(client)"
                    >
                      <Icon icon="i-lucide-pencil" class="size-4" />
                    </button>
                    <button
                      type="button"
                      class="rounded-lg p-2 text-n-ruby-11 hover:bg-n-ruby-3/30"
                      :title="t('TAXIS.CLIENTS.DELETE')"
                      @click="removeClient(client)"
                    >
                      <Icon icon="i-lucide-trash-2" class="size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="clients.length && !filteredClients.length"
          class="px-4 py-8 text-center text-sm text-n-slate-11"
        >
          {{ t('TAXIS.CLIENTS.NO_SEARCH_RESULTS') }}
        </div>
      </div>
    </div>

    <Dialog
      ref="clientDialogRef"
      type="edit"
      :title="
        persistedClientId
          ? t('TAXIS.CLIENTS.DIALOG_EDIT_TITLE')
          : t('TAXIS.CLIENTS.DIALOG_CREATE_TITLE')
      "
      width="lg"
      :show-cancel-button="false"
      :show-confirm-button="false"
      overflow-y-auto
      @close="onDialogClose"
    >
      <div class="flex flex-col gap-4">
        <div class="grid gap-4 sm:grid-cols-2">
          <Input
            v-model="form.first_name"
            :label="t('TAXIS.CLIENTS.FORM.FIRST_NAME')"
            :placeholder="t('TAXIS.CLIENTS.FORM.FIRST_NAME_PLACEHOLDER')"
          />
          <Input
            v-model="form.last_name"
            :label="t('TAXIS.CLIENTS.FORM.LAST_NAME')"
            :placeholder="t('TAXIS.CLIENTS.FORM.LAST_NAME_PLACEHOLDER')"
          />
        </div>
        <Input
          v-model="form.phone"
          :label="t('TAXIS.CLIENTS.FORM.PHONE')"
          :placeholder="t('TAXIS.CLIENTS.FORM.PHONE_PLACEHOLDER')"
        />

        <div class="flex w-full min-w-0 flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TAXIS.CLIENTS.FORM.PARTNERS') }}
          </label>
          <multiselect
            v-model="selectedPartners"
            class="[&_.multiselect__tags]:min-h-10"
            :options="partnersCatalog"
            track-by="id"
            label="name"
            multiple
            :close-on-select="false"
            :clear-on-select="false"
            hide-selected
            selected-label
            :placeholder="t('TAXIS.CLIENTS.FORM.PARTNERS_PLACEHOLDER')"
            :select-label="t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          />
        </div>

        <div class="border-t border-n-weak pt-4">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('TAXIS.CLIENTS.TRIPS.SECTION_TITLE') }}
          </h3>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ t('TAXIS.CLIENTS.TRIPS.SECTION_HELP') }}
          </p>

          <div
            v-if="trips.length"
            class="mt-3 max-h-48 space-y-2 overflow-y-auto rounded-lg border border-n-weak bg-n-alpha-2/50 p-2"
          >
            <div
              v-for="tr in trips"
              :key="tr.id"
              class="flex items-start justify-between gap-2 rounded-md bg-n-solid-1 px-3 py-2 text-sm"
            >
              <div class="min-w-0 flex-1">
                <div class="font-medium text-n-slate-12">
                  {{ formatDate(tr.trip_on) }}
                </div>
                <div v-if="tr.notes" class="mt-0.5 text-n-slate-11">
                  {{ tr.notes }}
                </div>
              </div>
              <button
                type="button"
                class="shrink-0 rounded-md p-1.5 text-n-ruby-11 hover:bg-n-ruby-3/30"
                :title="t('TAXIS.CLIENTS.TRIPS.REMOVE')"
                @click="removeTrip(tr)"
              >
                <Icon icon="i-lucide-x" class="size-4" />
              </button>
            </div>
          </div>

          <div class="mt-3 grid gap-3 sm:grid-cols-2">
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('TAXIS.CLIENTS.TRIPS.TRIP_DATE') }}
              </label>
              <input
                v-model="tripDraft.trip_on"
                type="date"
                class="h-10 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8 focus:ring-1 focus:ring-n-blue-8"
              />
            </div>
            <div class="sm:col-span-2">
              <TextArea
                v-model="tripDraft.notes"
                :label="t('TAXIS.CLIENTS.TRIPS.NOTES')"
                :placeholder="t('TAXIS.CLIENTS.TRIPS.NOTES_PLACEHOLDER')"
                :max-length="2000"
                min-height="4rem"
              />
            </div>
          </div>
          <div class="mt-2">
            <Button
              type="button"
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-plus"
              :label="t('TAXIS.CLIENTS.TRIPS.ADD_TRIP')"
              @click="addTrip"
            />
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex w-full justify-end gap-2">
          <Button
            variant="faded"
            color="slate"
            type="button"
            :label="t('TAXIS.CLIENTS.CANCEL')"
            @click="closeDialog"
          />
          <Button
            type="button"
            :label="t('TAXIS.CLIENTS.SAVE')"
            @click="saveClient"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
